import React, { useEffect, useState } from "react";
import GenericCard from "components/UI/GenericCard/GenericCard";
import { apiGetPerformanceStats } from "api/apiTrades";
import { useSelector } from "react-redux";
import { Button, Divider } from "antd";
import { toFixed } from "utils/helpers";

import SymbolCheckup from "./SymbolCheckup";
import OnlineUsers from "./OnlineUsers";
import NasdaqStatus from "./NasdaqStatus";
import TVChartContainer from "components/TVChartContainer/TVChartContainer";
import RecentTrades from "components/Home/RecentTrades";
import WinRateOverTime from "./WinRateOverTime";
import PortfolioProfitOverTime from "./PortfolioProfitOverTime";
import RecentMonthsPNL from "./RecentMonthsPNL";
import CountUp from "react-countup";
import { ReloadOutlined } from "@ant-design/icons";

const PerformanceStats = () => {
  const [performanceStats, setPerformanceStats] = useState(null);
  const accountType = useSelector((state) => state.main.accountType);
  const [loading, setLoading] = useState(false);

  const getPerformanceStats = async (isInitialLoad = false) => {
    try {
      if (!isInitialLoad) {
        setLoading(true);
      }
      const response = await apiGetPerformanceStats(accountType);
      setPerformanceStats(response);
    } catch (error) {
      console.error(error);
    } finally {
      if (!isInitialLoad) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    getPerformanceStats(true);
  }, [accountType]);

  const formatCurrency = (value) => {
    if (!value) return "$0.00";
    return `$${parseFloat(value).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatPercentage = (value) => {
    if (!value) return "0%";
    return `${toFixed(+value, 2)}%`;
  };

  const getColorClass = (value) => {
    if (!value) return "";
    const numValue = parseFloat(value);
    if (numValue === 0) return "";
    if (numValue > 0) return "green";
    return "red";
  };

  const getStreakColor = (type, streak) => {
    if (streak === 0) return "";
    if (type === "win") return "green";
    return "red";
  };

  return (
    <>
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <Divider orientation="left">Portfolio Performance</Divider>
        </div>
        <div className="flex items-center gap-2">
          <Button
            shape="circle"
            icon={<ReloadOutlined />}
            onClick={() => getPerformanceStats(false)}
            loading={loading}
          />
        </div>
      </div>
      <div className="space-y-4 pb-6">
        <section className="stats-grid">
          <GenericCard minHeight={120} title="Total P&L" disableToggle>
            <div className="flex flex-col gap-2">
              <div
                className={`text-3xl font-bold ${getColorClass(
                  performanceStats?.totalPnl
                )}`}
              >
                <CountUp
                  prefix="$"
                  end={performanceStats?.totalPnl || 0}
                  separator=","
                  duration={2}
                  className={getColorClass(performanceStats?.totalPnl)}
                />
              </div>
              <div className="text-sm">All time profit/loss</div>
            </div>
          </GenericCard>

          <GenericCard minHeight={85} title="Win Rate" disableToggle>
            <div className="flex flex-col gap-2">
              <div className="text-3xl font-bold text-blue-600">
                {formatPercentage(performanceStats?.winRate)}
              </div>
              <div className="text-sm">Success rate</div>
            </div>
          </GenericCard>

          <GenericCard minHeight={85} title="Total Trades" disableToggle>
            <div className="flex flex-col gap-2">
              <div className="text-3xl font-bold text-gray-800">
                {performanceStats?.closedTrades || 0}
              </div>
              <div className="text-sm">Closed trades</div>
            </div>
          </GenericCard>

          <GenericCard minHeight={85} title="Active Positions" disableToggle>
            <div className="flex flex-col gap-2">
              <div className="text-3xl font-bold text-purple-600">
                {performanceStats?.activePositions || 0}
              </div>
              <div className="text-sm">
                Value: {formatCurrency(performanceStats?.activePositionsValue)}
              </div>
            </div>
          </GenericCard>

          <GenericCard minHeight={85} title="Unrealized P&L" disableToggle>
            <div className="flex flex-col gap-2">
              <div
                className={`text-2xl font-bold ${getColorClass(
                  performanceStats?.unrealizedPnl
                )}`}
              >
                {formatCurrency(performanceStats?.unrealizedPnl)}
              </div>
              <div
                className={`pt-1 text-sm ${getColorClass(
                  performanceStats?.unrealizedPnlPercentage
                )}`}
              >
                {formatPercentage(performanceStats?.unrealizedPnlPercentage)}
              </div>
            </div>
          </GenericCard>
          <GenericCard minHeight={85} title="Profit Factor" disableToggle>
            <div className="flex flex-col gap-2">
              <div className="text-2xl font-bold text-indigo-600">
                {performanceStats?.profitFactor || 0}
              </div>
            </div>
          </GenericCard>
          <GenericCard minHeight={85} title="Current Streak" disableToggle>
            <div className="flex flex-col gap-2">
              {performanceStats?.currentStreak === 0 ? (
                <div className="text-2xl font-bold text-gray-600">0</div>
              ) : (
                <div
                  className={`text-2xl font-bold ${getStreakColor(
                    performanceStats?.currentStreakType,
                    performanceStats?.currentStreak
                  )}`}
                >
                  {performanceStats?.currentStreak || 0}{" "}
                  {performanceStats?.currentStreakType || "win"}s
                </div>
              )}
            </div>
          </GenericCard>
        </section>

        <div className="content-grid">
          <div className="flex flex-col gap-4">
            <SymbolCheckup />
            <OnlineUsers />
            <NasdaqStatus />
          </div>
          <RecentMonthsPNL performanceStats={performanceStats} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[2.8fr_1.5fr] gap-6">
          <PortfolioProfitOverTime
            pnlOverTime={performanceStats?.pnlOverTime}
          />
          <RecentTrades trades={performanceStats?.lastTrades} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[1.25fr_1fr] gap-6">
          <GenericCard className="table-card [&>.content]:p-0">
            <TVChartContainer />
          </GenericCard>
          <WinRateOverTime
            winRateOverTime={performanceStats?.winRateOverTime}
          />
        </div>
      </div>
    </>
  );
};

export default PerformanceStats;
